import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Observable, shareReplay, switchMap } from 'rxjs';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { CmsArticle, CmsPicture, CmsTaxonomyGeneral, CmsVideo } from '@fcom/core-api';
import { isNotBlank } from '@fcom/core/utils/utils';
import { type IconName } from '@fcom/ui-components';

export const allowedBackgrounds: Record<string, string> = {
  'nordic-blue-25-bg': 'nordic-blue-25-bg signal-information-notification-bg',
  'nordic-blue-50-bg': 'nordic-blue-50-bg signal-information-notification-bg',
  'nordic-blue-50-bg signal-information-notification-bg': 'nordic-blue-50-bg signal-information-notification-bg',
  'signal-information-notification-bg': 'signal-information-notification-bg',
};

@Component({
  selector: 'cms-rich-text',
  template: `
    <fin-rich-text
      [class]="hostClasses()"
      [content$]="currentContent$"
      [headerLevel]="headerLevel()"
      [headerStyles]="headerStyles()"
      [hideMediaContentImage]="hideMediaContentImage()"
      [icons$]="icons() | finToObservable"
      [media$]="media() | finToObservable"
      [scaleImage]="scaleImage()"
      [styleClasses$]="styleClasses() | finToObservable"
      [title$]="title() | finToObservable"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsRichTextComponent {
  content$ = input.required<Observable<CmsArticle>>();
  headerLevel = input<number>();
  headerStyles = input<string[]>([]);
  scaleImage = input(true);
  extraClasses = input('mr-large-b');
  hideMediaContentImage = input(false);

  // This observable flattens the content$ input and shares the latest value to fin-rich-text component
  protected readonly currentContent$ = toObservable(this.content$).pipe(
    switchMap((c$) => c$),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected content = toSignal(this.currentContent$);

  title = computed(() => {
    const content = this.content();
    const hasNoTitleTag = (content?.subjectTaxonomyTags ?? []).includes(CmsTaxonomyGeneral.NO_TITLE);
    return !hasNoTitleTag && isNotBlank(content?.teaserTitle) ? content.teaserTitle : undefined;
  });

  media = computed(() => (this.content()?.media ?? []) as CmsPicture[] | CmsVideo[]);

  icons = computed<Array<{ name: IconName; category?: IconLibrary }> | undefined>(() => {
    const localSettings = this.content()?.localSettings;
    if (!localSettings?.iconName) {
      return undefined;
    }
    const names = localSettings.iconName;
    const nameArr = Array.isArray(names) ? names : [names];
    const categories = localSettings?.iconCategory;
    const categoryArr = Array.isArray(categories) ? categories : [categories];
    return nameArr.map((name, index) => {
      return {
        name,
        category: categoryArr?.[index] ?? IconLibrary.SVG_LIBRARY,
      };
    });
  });

  styleClasses = computed(() =>
    (this.content()?.subjectTaxonomyTags ?? []).filter((tag) => tag.startsWith('cmstyle-'))
  );

  protected hostClasses = computed(() => {
    const bg = this.content()?.subjectTaxonomyTags?.find((tag) => tag in allowedBackgrounds);

    if (bg) {
      const bgClasses = allowedBackgrounds[bg];

      return `block ${this.extraClasses()} ${bgClasses} pr-small rounded-medium`;
    }

    return `block ${this.extraClasses()}`;
  });
}
